import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { motion } from "motion/react";
import { api, useAction, useApi } from "../api.ts";
import { Button, ErrorText, Field, Icon, Loading } from "../ui/index.tsx";
import { AuthLayout } from "../layouts.tsx";
import { authItem, PasswordInput } from "./Login.tsx";
import { StrengthMeter } from "./Register.tsx";
import type { ResetPasswordRequest } from "../../shared/types.ts";

export default function ResetPassword() {
  const { token = "" } = useParams();
  const check = useApi<{ ok: true }>(`/auth/reset/${encodeURIComponent(token)}`);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);

  const submit = useAction(async () => {
    const body: ResetPasswordRequest = { token, password };
    await api.post("/auth/reset", body);
    setDone(true);
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    void submit.run();
  };

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You've been signed out everywhere. Sign in with your new password.">
        <motion.div variants={authItem} role="status" className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border border-ok/30 bg-ok/10 px-3 py-3 text-sm text-ok">
            <Icon name="check" className="size-4 shrink-0" />
            <span>Your password has been changed.</span>
          </div>
          <Link to="/login"><Button variant="primary" size="lg" className="w-full">Sign in</Button></Link>
        </motion.div>
      </AuthLayout>
    );
  }

  if (check.loading && !check.data) {
    return <AuthLayout title="Checking your link"><Loading /></AuthLayout>;
  }

  if (check.error) {
    return (
      <AuthLayout title="Link expired" footer={<Link to="/login" className="text-accent hover:underline">Back to sign in</Link>}>
        <motion.div variants={authItem} className="space-y-4">
          <div role="alert"><ErrorText>{check.error.message}</ErrorText></div>
          <Link to="/forgot"><Button variant="primary" size="lg" className="w-full">Request a new link</Button></Link>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="Pick something you don't use anywhere else."
      footer={<Link to="/login" className="hover:text-fg">Back to sign in</Link>}>
      <form onSubmit={onSubmit} className="space-y-4">
        <motion.div variants={authItem}>
          <Field label="New password" hint="8 to 128 characters">
            <PasswordInput label="New password" autoComplete="new-password" autoFocus required minLength={8} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <StrengthMeter password={password} />
        </motion.div>
        <motion.div variants={authItem}>
          <Field label="Confirm password" error={mismatch ? "Passwords don't match" : null}>
            <PasswordInput label="Confirm password" autoComplete="new-password" required minLength={8} maxLength={128} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
        </motion.div>
        <div role="alert" className="empty:hidden"><ErrorText>{submit.error}</ErrorText></div>
        <motion.div variants={authItem}>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={submit.pending}>Reset password</Button>
        </motion.div>
      </form>
    </AuthLayout>
  );
}
